import { useEffect, useState } from "react";
import { Box, Typography, Table, TableRow, TableBody, TableHead, TableContainer, Pagination, Chip, IconButton, Dialog, DialogTitle, DialogContent, Tabs, Tab, Tooltip } from "@mui/material";
import Button from "components/atoms/Button";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { MODAL, modalActions } from "redux/slices/modal";
import AssetManagementService from "services/assetManagement.service";
import { assetManagementActions } from "redux/slices/assetManagement/assetManagementSlice";
import TableLoadingWrapper from "components/templates/TableLoadingWrapper";
import { StyledTableCell, StyledTableRow } from "components/templates/Tables";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DateService from "utils/date.util";
import CarAngleIcon from "components/molecules/CarAngleIcon/CarAngleIcon";
import ECarsFilters from "./ECarsFilters";
import { openPrivateFile } from "utils/openPrivateFile.util";

export default function ECarsListing() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const loading = useAppSelector((s) => s.assetManagement.loading);
    const eCarsListingData = useAppSelector((s) => s.assetManagement.eCarsListingData) as any[];
    const eCarsCount = useAppSelector((s) => s.assetManagement.eCarsCount) || 0;
    const eCarsFilters = useAppSelector((s) => s.assetManagement.eCarsFilters);
    const eCarsRefresh = useAppSelector((s) => s.assetManagement.eCarsRefresh) || 0;
    const eCarsRefreshLoader = useAppSelector((s) => s.assetManagement.eCarsRefreshLoader) || false;
    
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [selectedECar, setSelectedECar] = useState<any>(null);
    const [selectedAngle, setSelectedAngle] = useState<string>("");
    const [selectedType, setSelectedType] = useState<"taken" | "returned">("taken");

    useEffect(() => {
        return () => {
            dispatch(assetManagementActions.resetECarsPage());
        };
    }, [dispatch]);

    useEffect(() => {
        AssetManagementService.getECarsListing(eCarsFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eCarsFilters, eCarsRefresh]);

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">E Cars Listing</Typography>
                <Button
                    variant="contained"
                    onClick={() => {
                        navigate("/add-e-car");
                    }}
                >
                    Add E Car
                </Button>
            </Box>

            <ECarsFilters />

            <div className="pagination-list">
                <p>{eCarsCount > 0 && `${eCarsCount} E Car${eCarsCount > 1 ? "s" : ""}`}</p>
                {eCarsCount > 0 && (
                    <Pagination
                        page={eCarsFilters.page}
                        disabled={eCarsRefreshLoader}
                        count={Math.ceil(eCarsCount / eCarsFilters.page_size)}
                        onChange={(_e, page) => dispatch(assetManagementActions.setECarsPage(page))}
                    />
                )}
            </div>

            <TableContainer>
                <Table
                    aria-label="e cars listing table"
                    sx={{
                        minWidth: "100%",
                        borderSpacing: "0 10px",
                        borderBottomWidth: "0px",
                        borderCollapse: "separate",
                    }}
                >
                    <TableLoadingWrapper
                        coloumns={9}
                        loading={loading}
                        length={eCarsRefreshLoader ? 0 : eCarsListingData.length}
                        message="No E cars found"
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Date</StyledTableCell>
                                <StyledTableCell>Driver</StyledTableCell>
                                <StyledTableCell>Vehicle</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="center">Images</StyledTableCell>
                                <StyledTableCell>Notes</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {eCarsListingData.map((eCar: any, index: number) => {
                                const driverName = eCar.driver_id 
                                    ? `${eCar.driver_id.first_name || ""} ${eCar.driver_id.last_name || ""}`.trim()
                                    : "-";
                                const startDate = eCar.taken_time 
                                    ? DateService.getDateString(eCar.taken_time, "DD.MM.YYYY")
                                    : "-";
                                const startTime = eCar.taken_time 
                                    ? DateService.getTimeString(eCar.taken_time)
                                    : "";
                                const endDate = eCar.return_time 
                                    ? DateService.getDateString(eCar.return_time, "DD.MM.YYYY")
                                    : null;
                                const endTime = eCar.return_time 
                                    ? DateService.getTimeString(eCar.return_time)
                                    : "";
                                
                                const vehicleInfo = eCar.vehicle_id 
                                    ? `${eCar.vehicle_id.license_plate || ""}${eCar.vehicle_id.model ? ` - ${eCar.vehicle_id.model}` : ""}`.trim()
                                    : "-";
                                
                                
                                return (
                                    <StyledTableRow key={eCar._id || index}>
                                        <StyledTableCell>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                <Typography variant="body2">
                                                    <strong>Taken:</strong>
                                                    <br />
                                                    {startDate}
                                                    {startTime && (
                                                        <>
                                                            <br />
                                                            {startTime}
                                                        </>
                                                    )}
                                                </Typography>
                                                {endDate && (
                                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                                                        <strong>Return:</strong>
                                                        <br />
                                                        {endDate}
                                                        {endTime && (
                                                            <>
                                                                <br />
                                                                {endTime}
                                                            </>
                                                        )}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell>{driverName}</StyledTableCell>
                                        <StyledTableCell>{vehicleInfo}</StyledTableCell>

                                        <StyledTableCell>
                                            <Chip
                                                size="small"
                                                label={eCar.status === "active" ? "Active" : "Returned"}
                                                sx={{
                                                    backgroundColor: eCar.status === "active" 
                                                        ? "rgba(76, 175, 80, 0.12)" 
                                                        : "rgba(33, 150, 243, 0.12)",
                                                    color: eCar.status === "active" 
                                                        ? "rgba(76, 175, 80, 0.9)" 
                                                        : "rgba(33, 150, 243, 0.9)",
                                                    border: eCar.status === "active" 
                                                        ? "1px solid rgba(76, 175, 80, 0.3)" 
                                                        : "1px solid rgba(33, 150, 243, 0.3)",
                                                }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                                                {/* Taken Images */}
                                                {eCar.images?.taken && (
                                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                                                        {["front", "back", "right", "left", "other"].map((angle: string) => {
                                                            const angleData = eCar.images?.taken?.[angle];
                                                            const hasImages = angleData?.images && angleData.images.length > 0;
                                                            if (!hasImages) return null;
                                                            
                                                            return (
                                                                <Tooltip key={angle} title={`${angleData.images.length} ${angle.charAt(0).toUpperCase() + angle.slice(1)} Image${angleData.images.length > 1 ? "s" : ""} (Taken)`}>
                                                                    <Box
                                                                        onClick={() => {
                                                                            setSelectedECar(eCar);
                                                                            setSelectedAngle(angle);
                                                                            setSelectedType("taken");
                                                                            setImageDialogOpen(true);
                                                                        }}
                                                                        sx={{ cursor: "pointer" }}
                                                                    >
                                                                        <CarAngleIcon angle={angle} size={40} hasImages={true} />
                                                                    </Box>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                
                                                {/* Returned Images */}
                                                {eCar.images?.returned && (
                                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                                                        {["front", "back", "right", "left", "other"].map((angle: string) => {
                                                            const angleData = eCar.images?.returned?.[angle];
                                                            const hasImages = angleData?.images && angleData.images.length > 0;
                                                            if (!hasImages) return null;
                                                            
                                                            return (
                                                                <Tooltip key={angle} title={`${angleData.images.length} ${angle.charAt(0).toUpperCase() + angle.slice(1)} Image${angleData.images.length > 1 ? "s" : ""} (Returned)`}>
                                                                    <Box
                                                                        onClick={() => {
                                                                            setSelectedECar(eCar);
                                                                            setSelectedAngle(angle);
                                                                            setSelectedType("returned");
                                                                            setImageDialogOpen(true);
                                                                        }}
                                                                        sx={{ cursor: "pointer" }}
                                                                    >
                                                                        <CarAngleIcon angle={angle} size={40} hasImages={true} />
                                                                    </Box>
                                                                </Tooltip>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                
                                                {(!eCar.images?.taken || Object.keys(eCar.images.taken).length === 0) && 
                                                 (!eCar.images?.returned || Object.keys(eCar.images.returned).length === 0) && (
                                                    <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                {eCar.taken_notes && (
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary", display: "block", mb: 0.5 }}>
                                                            Taken Notes:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
                                                            {eCar.taken_notes}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {eCar.return_notes && (
                                                    <Box>
                                                        <Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary", display: "block", mb: 0.5 }}>
                                                            Return Notes:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
                                                            {eCar.return_notes}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {!eCar.taken_notes && !eCar.return_notes && (
                                                    <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic", fontSize: "0.85rem" }}>
                                                        -
                                                    </Typography>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => {
                                                        navigate(`/update-e-car/${eCar._id}`);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                {!eCar.end_date && eCar.status === "active" && (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            dispatch(
                                                                modalActions.openModal({
                                                                    type: MODAL.RETURN_ECAR,
                                                                    data: { eCar },
                                                                    width: "800px",
                                                                })
                                                            );
                                                        }}
                                                    >
                                                        Return
                                                    </Button>
                                                )}
                                                {eCar.end_date && (
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: "rgba(158, 158, 158, 0.8)",
                                                            fontStyle: "italic"
                                                        }}
                                                    >
                                                        Returned
                                                    </Typography>
                                                )}
                                            </Box>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </TableLoadingWrapper>
                </Table>
            </TableContainer>

            {/* Angle Images Dialog */}
            <Dialog
                open={imageDialogOpen}
                onClose={() => {
                    setImageDialogOpen(false);
                    setSelectedAngle("");
                    setSelectedType("taken");
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CarAngleIcon angle={selectedAngle} size={40} />
                            <Typography variant="h6">
                                {selectedAngle.charAt(0).toUpperCase() + selectedAngle.slice(1)} - {selectedType === "taken" ? "Taken" : "Returned"}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => {
                            setImageDialogOpen(false);
                            setSelectedAngle("");
                            setSelectedType("taken");
                        }} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedECar && selectedAngle && (
                        <>
                            {(() => {
                                const angleData = selectedECar.images?.[selectedType]?.[selectedAngle];
                                const images = angleData?.images || [];
                                const notes = angleData?.notes || "";
                                
                                return (
                                    <>
                                        {notes && (
                                            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                                    Notes:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {notes}
                                                </Typography>
                                            </Box>
                                        )}
                                        
                                        {images.length > 0 ? (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                                                {images.map((imageUrl: string, index: number) => (
                                                    <Box
                                                        key={index}
                                                        onClick={() => openPrivateFile(imageUrl, "private")}
                                                        sx={{
                                                            width: "150px",
                                                            height: "150px",
                                                            border: "1px solid #ddd",
                                                            borderRadius: 1,
                                                            cursor: "pointer",
                                                            overflow: "hidden",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            bgcolor: "#f5f5f5",
                                                            "&:hover": {
                                                                opacity: 0.8,
                                                                borderColor: "primary.main",
                                                            },
                                                        }}
                                                    >
                                                        <Box
                                                            component="img"
                                                            src={imageUrl}
                                                            alt={`${selectedAngle} image ${index + 1}`}
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography color="textSecondary" sx={{ textAlign: "center", py: 4 }}>
                                                No images for this angle
                                            </Typography>
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

